
import { useState, useRef } from "react";
import { X, Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface AddCategoryPopoverProps {
  onAddCategory: (categoryName: string, iconSrc?: string) => void;
}

export const AddCategoryPopover = ({ onAddCategory }: AddCategoryPopoverProps) => {
  const [newCategory, setNewCategory] = useState("");
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image under 2MB",
          variant: "destructive",
        });
        return;
      }
      
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddCategory = async () => {
    if (newCategory.trim()) {
      try {
        let iconSrc = null;
        
        if (selectedFile) {
          const reader = new FileReader();
          iconSrc = await new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(selectedFile);
          });
        }
        
        onAddCategory(newCategory.trim(), iconSrc || undefined);
        setNewCategory("");
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsAddPopoverOpen(false);
        toast({
          title: "Success",
          description: "Category has been added successfully.",
        });
      } catch (error) {
        console.error('Error adding category:', error);
        toast({
          title: "Error",
          description: "Failed to add category. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Error",
        description: "Please enter a category name.",
        variant: "destructive",
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Popover open={isAddPopoverOpen} onOpenChange={setIsAddPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-gray-100"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-68">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-lg font-semibold">Add Category</Label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-gray-100"
                  onClick={() => setIsAddPopoverOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label>Category Name</Label>
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                />
              </div>
              <div className="space-y-2">
                <Label>Category Icon (Optional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={triggerFileInput}
                    className="flex-1"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select Icon
                  </Button>
                  {selectedFile && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {previewUrl && (
                  <div className="mt-2 flex justify-center">
                    <div className="border rounded-md p-2 bg-gray-50 w-20 h-20 flex items-center justify-center">
                      <img 
                        src={previewUrl} 
                        alt="Category icon preview" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white"
                onClick={handleAddCategory}
                disabled={!newCategory.trim()}
              >
                Add Category
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs text-gray-500">Add New Category</p>
      </TooltipContent>
    </Tooltip>
  );
};
